/**
 * "Deploy a node" on a project's page (D447): makes the project's own join key for one pool of an approved cluster that
 * serves it. The key is shown once, with Copy. It works for one node, lasts a day at most, and the node that joins with it
 * works for this project and no other.
 */

import type { ApprovedCluster } from '@aioe/shared';
import { useState } from 'react';

import { computeRoutes, problemText } from '../../api/client.ts';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { Button, ChoiceSelect, ErrorNote } from '../ui.tsx';
import { FormField, FormSheet } from '../ui/FormSheet.tsx';

export function DeployNodeSheet({ projectId, clusters, onClose }: { projectId: string; clusters: ApprovedCluster[]; onClose: () => void }) {
  const [clusterId, setClusterId] = useState(clusters[0]?.id ?? '');
  const cluster = clusters.find((c) => c.id === clusterId);
  const [pool, setPool] = useState(cluster?.nodePools?.[0]?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [made, setMade] = useState<{ key: string; expiresAt: string }>();
  const [copied, setCopied] = useState(false);
  const pools = Object.fromEntries((cluster?.nodePools ?? []).map((p) => [p.name, p.name]));

  const make = async () => {
    setBusy(true);
    setError(undefined);
    try {
      setMade(await computeRoutes.projectJoinKey(projectId, { clusterId, nodePool: pool }));
    } catch (e) {
      setError(problemText(e));
    }
    setBusy(false);
  };

  return (
    <FormSheet
      width="md"
      title="Deploy a node"
      description="Make a key for a node to join one of this project’s clusters. The node works for this project and no other."
      onClose={onClose}
      footer={
        made ? (
          <Button variant="primary" onPress={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onPress={() => void make()} isPending={busy} isDisabled={!cluster || !pool}>
              Make a key
            </Button>
          </>
        )
      }
    >
      {made ? (
        <div className="grid gap-3">
          <FormField label="Join key" hint="Shown once. Copy it now: it cannot be shown again.">
            <div className="flex items-center gap-2">
              <code className="mono min-w-0 flex-1 break-all rounded-lg bg-surface-secondary p-2 text-xs">{made.key}</code>
              <Button
                size="sm"
                variant="secondary"
                onPress={() => void navigator.clipboard?.writeText(made.key).then(() => (setCopied(true), setTimeout(() => setCopied(false), 1500)))}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </FormField>
          <p className="text-xs text-muted">It admits one node, then stops working. It lasts 24 hours at most: this one expires {dateTime(made.expiresAt)}.</p>
        </div>
      ) : (
        <>
          <FormField label="Cluster">
            <ChoiceSelect
              label="Cluster"
              className="w-full"
              value={clusterId}
              options={Object.fromEntries(clusters.map((c) => [c.id, c.name]))}
              onChange={(id) => {
                setClusterId(id);
                setPool(clusters.find((c) => c.id === id)?.nodePools?.[0]?.name ?? '');
              }}
            />
          </FormField>
          <FormField label="Node pool" hint="The node joins this pool and carries its tags.">
            <ChoiceSelect label="Node pool" className="w-full" value={pool} options={pools} onChange={setPool} />
          </FormField>
          <p className="text-xs text-muted">The key is single use and lasts 24 hours. It is shown once, so copy it straight away.</p>
          <ErrorNote error={error} />
        </>
      )}
    </FormSheet>
  );
}
